import { onUnmounted } from 'vue';

/**
 * Composable that creates GSAP ScrollTrigger-driven animations
 * for the four homepage discipline panels.
 *
 * Both desktop and mobile use the same scroll flow: simple fade/parallax
 * triggers with no pinning. Desktop additionally includes the comms
 * white-overlay transition for the header-light state.
 */
export function useDisciplineAnimations(gsap) {
  let ctx = null;

  // commsOverlay: fade the comms panel out to the light section that follows it
  // on the homepage. Off where the panel stands alone.
  function initDisciplineAnimations({ commsOverlay = true } = {}) {
    ctx = gsap.context(() => {
      const mm = gsap.matchMedia();

      // ─── Desktop (no pin — same scroll flow as mobile) ────
      mm.add('(min-width: 1025px)', () => {
        initMobilePanels();
        if (commsOverlay) initCommsOverlay();
      });

      // ─── Mobile ───────────────────────────────────────────
      mm.add('(max-width: 1024px)', () => {
        initMobilePanels();
      });
    });
  }

  // ═══════════════════════════════════════════════════════════
  //  PANELS — simplified fades (no pin, no marquee)
  // ═══════════════════════════════════════════════════════════
  function initMobilePanels() {
    const containers = document.querySelectorAll(
      '.panel-brands-container, .panel-shopper-container, .panel-social-container, .panel-comms-container'
    );

    containers.forEach((container) => {
      const copy = container.querySelector('.discipline-copy');
      const thumbsWrapper = container.querySelector(
        '[class*="__brand-thumbs"], [class*="__shopper-thumbs"], [class*="__social-thumbs"], [class*="__comms-thumbs"]'
      );
      const titleImg = container.querySelector(
        '[class*="__brand-title"] img, [class*="__shopper-title"] img, [class*="__social-title"] img, [class*="__comms-title"] img'
      );
      const textContainer = container.querySelector('[class*="__text-container"]');

      // Text container fade in
      if (textContainer) {
        gsap.fromTo(textContainer,
          { opacity: 0, y: 20 },
          {
            opacity: 1, y: 0, duration: 0.4,
            scrollTrigger: {
              trigger: textContainer,
              start: 'top 90%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      }

      // Thumbnails subtle parallax
      if (thumbsWrapper) {
        gsap.fromTo(thumbsWrapper,
          { y: 40 },
          {
            y: -20, ease: 'none',
            scrollTrigger: {
              trigger: thumbsWrapper,
              start: 'top bottom',
              end: 'bottom top',
              scrub: true,
            },
          }
        );
      }

      // Title opacity + scale
      if (titleImg) {
        gsap.fromTo(titleImg,
          { opacity: 0, scale: 0.9 },
          {
            opacity: 1, scale: 1, duration: 0.35,
            scrollTrigger: {
              trigger: titleImg,
              start: 'top 90%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      }

      // Copy fade
      if (copy) {
        gsap.fromTo(copy,
          { opacity: 0, y: 20 },
          {
            opacity: 1, y: 0, duration: 0.4,
            scrollTrigger: {
              trigger: copy,
              start: 'top 90%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      }
    });
  }

  // ═══════════════════════════════════════════════════════════
  //  COMMS OVERLAY — standalone (no pin) for header-light transition
  // ═══════════════════════════════════════════════════════════
  function initCommsOverlay() {
    const commsContainer = document.querySelector('.panel-comms-container');
    if (!commsContainer) return;

    const whiteOverlay = commsContainer.querySelector('.panel-comms__white-overlay');
    const header = document.querySelector('.header');

    if (whiteOverlay) {
      gsap.fromTo(whiteOverlay,
        { opacity: 0 },
        {
          opacity: 1,
          ease: 'power1.in',
          scrollTrigger: {
            trigger: commsContainer,
            start: 'bottom 30%',
            end: 'bottom top',
            scrub: true,
            onEnter: () => header && header.classList.add('header--light'),
            onLeaveBack: () => header && header.classList.remove('header--light'),
          },
        }
      );
    }
  }

  // ─── Cleanup ──────────────────────────────────────────────
  function cleanup() {
    if (ctx) {
      ctx.revert();
      ctx = null;
    }
  }

  onUnmounted(cleanup);

  return { initDisciplineAnimations, cleanup };
}
